import { format } from 'date-fns'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SegmentItem } from '@ui/components/SegmentedControl/SegmentItem'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { CalendarPopup } from '@ui/v2/Calendar/CalendarPopup'

import { useGAEvents } from '@utils/callGAEvents'

import { dateFnsLocales } from '@global/locale/Locale'

import { useCalendarRangeOptions } from '@hooks/useCalendarRangeOptions'

import { last30Days, oneWeek, oneYear, twoWeeks } from '../constants/performance'

import styles from './DateFilter.module.scss'

enum DateFilterType {
  OneWeek = 'one-week',
  TwoWeeks = 'two-weeks',
  OneMonth = 'one-month',
  OneYear = 'one-year',
  Custom = 'custom',
}

interface DateFiltersProps {
  dateRange: [Date, Date]
  onDateRangeSelect: (range: [Date, Date]) => void
}

interface DateFilterOptions {
  name: string
  id: DateFilterType
  value?: () => [Date, Date]
}

const DATE_FORMAT = 'd MMM'

export const DateFilter: FC<DateFiltersProps> = (props) => {
  const { dateRange, onDateRangeSelect } = props
  const [startDate, endDate] = dateRange

  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const { callGAEvents } = useGAEvents()

  const [currentTab, setCurrentTab] = useState<DateFilterType>(DateFilterType.OneWeek)

  const dateFilterOptions: DateFilterOptions[] = [
    {
      id: DateFilterType.OneWeek,
      name: t('IB.Dashboard.7D'),
      value: oneWeek,
    },
    {
      id: DateFilterType.TwoWeeks,
      name: t('IB.Dashboard.14D'),
      value: twoWeeks,
    },
    {
      id: DateFilterType.OneMonth,
      name: t('IB.Dashboard.1M'),
      value: last30Days,
    },
    {
      id: DateFilterType.OneYear,
      name: t('IB.Dashboard.1Y'),
      value: oneYear,
    },
    {
      id: DateFilterType.Custom,
      name: `${format(startDate, DATE_FORMAT, {
        locale: dateFnsLocales[locale],
      })} - ${format(endDate, DATE_FORMAT, { locale: dateFnsLocales[locale] })}`,
    },
  ]

  const handleTabChange = (tab: DateFilterType): void => {
    switch (tab) {
      case DateFilterType.OneWeek:
        callGAEvents().IBDashboard.QuickPerformance.sevenDays()
        break
      case DateFilterType.TwoWeeks:
        callGAEvents().IBDashboard.QuickPerformance.fourteenDays()
        break
      case DateFilterType.OneMonth:
        callGAEvents().IBDashboard.QuickPerformance.oneMonth()
        break
      case DateFilterType.OneYear:
        callGAEvents().IBDashboard.QuickPerformance.oneYear()
        break
    }

    const selectedOption = dateFilterOptions.find((option) => option.id === tab)
    if (selectedOption?.value) {
      onDateRangeSelect(selectedOption.value())
    }
  }

  const handleCustomDateRangeSelect = (range: [Date, Date]): void => {
    onDateRangeSelect(range)
    setCurrentTab(DateFilterType.Custom)
    callGAEvents({
      start_date: range[0].toDateString(),
      end_date: range[1].toDateString(),
    }).IBDashboard.QuickPerformance.calendar()
  }

  const calendarRangeOptions = useCalendarRangeOptions()

  useEffect(() => {
    const selectedOption = dateFilterOptions.find((option) => {
      return (
        option.value?.()?.[0].getTime() === startDate?.getTime() &&
        option.value?.()?.[1].getTime() === endDate?.getTime()
      )
    })

    setCurrentTab(selectedOption?.id || DateFilterType.Custom)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  return (
    <div className={styles.dateFilter}>
      <SegmentedControl size='md'>
        {dateFilterOptions.map((x) =>
          x.id === DateFilterType.Custom ? (
            <CalendarPopup
              key={x.id}
              onConfirm={handleCustomDateRangeSelect}
              rangeOptions={calendarRangeOptions}
              startDate={startDate}
              endDate={endDate}
              monthsShown={2}
              popupClassName={styles.calendarPopup}
              maxDate={new Date()}
            >
              <SegmentItem
                key={x.id}
                tab={{ name: currentTab === x.id ? x.name : t('Custom'), id: x.id }}
                isActive={x.id === currentTab}
                size='md'
                onSelect={() => handleTabChange(x.id)}
              />
            </CalendarPopup>
          ) : (
            <SegmentItem
              key={x.id}
              tab={x}
              isActive={x.id === currentTab}
              size='md'
              onSelect={() => handleTabChange(x.id)}
            />
          )
        )}
      </SegmentedControl>
    </div>
  )
}
